import React, { useState, useEffect, useRef } from 'react';
import { isValid, isValidArrayWithValues } from '../../utils/utilities';
import { getReadableDate, getLocalDateAndTime } from '../../utils/date.utils';
import { fetchTransactionsByPnr } from '../../actions/agents.actions';
import { Table, Row, Col, Button, Tag, Tooltip } from 'antd';
import moment from 'moment';
import { PrinterOutlined } from '@ant-design/icons';
import { roundNumber } from '../../utils/math.utils';
import { useReactToPrint } from 'react-to-print';
import './agent-ledger.style.scss';

export const ExpandRowComponent = (props) => {
    const {
        expandRecord,
        agentType,
        setIsPaymentModalVisible,
        setSelectedAgentForPayment,
        dateRange
    } = props;

    const [expandedRowData, setExpandedRowData] = useState(null);
    const [isValidExpandedRowData, setIsValidExpandedRowData] = useState(false);
    const [formattedTransactionSummaryData, setFormattedTransactionSummaryData] = useState(null);
    const [filteredConfirmedData, setFilteredConfirmedData] = useState(null);
    const [filteredCancelledData, setFilteredCancelledData] = useState(null);
    const [confirmedTotals, setConfirmedTotals] = useState({
        totalFare: 0,
        totalGST: 0,
        totalCommission: 0,
        netAmount: 0,
        amountReceived: 0,
        balanceDue: 0
    });
    const [cancelledTotals, setCancelledTotals] = useState({
        totalFare: 0,
        totalGST: 0,
        totalPenalty: 0,
        totalPenaltyCommission: 0,
        netAmount: 0,
        amountReceived: 0,
        balanceDue: 0
    });
    const printRef = useRef();

    function transformTicketSummaryToAgentTransactionSummary(ticketSummary) {
        const pnrGroups = {}
        // Process confirmed tickets
        ticketSummary.confirmed.forEach(ticket => {
            const pnr = ticket.pnr
            if (ticket.status == "Paid") {
                return;
            }
            if (!pnrGroups[pnr]) {
                pnrGroups[pnr] = {
                    pnr: pnr,
                    ticket_id: ticket.ticket_id,
                    start_date: ticket.start_date,
                    confirmedTickets: [],
                    cancelledTickets: [],
                    totalConfirmedAmount: 0,
                    totalCancelledAmount: 0,
                    totalReceivedAmount: 0,
                    commonInfo: {
                        origin: ticket.origin,
                        destination: ticket.destination,
                        tripDetails: ticket.tripDetails,
                        bookingDate: ticket.bookingDate,
                        startDate: ticket.start_date,
                        passengerDetails: ticket.passengerDetails,
                    }
                }
            }
            pnrGroups[pnr].confirmedTickets.push(ticket)
            pnrGroups[pnr].totalConfirmedAmount += ticket.netAmount
            pnrGroups[pnr].totalReceivedAmount += ticket.amountReceived
        })
        // Process cancelled tickets
        ticketSummary.cancelled.forEach(ticket => {
            const pnr = ticket.pnr
            if (ticket.status == "Paid") {
                return;
            }
            if (!pnrGroups[pnr]) {
                pnrGroups[pnr] = {
                    ticket_id: ticket.ticket_id,
                    pnr: pnr,
                    start_date: ticket.start_date,
                    confirmedTickets: [],
                    cancelledTickets: [],
                    totalConfirmedAmount: 0,
                    totalCancelledAmount: 0,
                    totalReceivedAmount: 0,
                    commonInfo: {
                        origin: ticket.origin,
                        destination: ticket.destination,
                        tripDetails: ticket.tripDetails,
                        bookingDate: ticket.bookingDate,
                        startDate: ticket.start_date,
                        passengerDetails: ticket.passengerDetails,
                    }
                }
            }
            pnrGroups[pnr].cancelledTickets.push(ticket)
            pnrGroups[pnr].totalCancelledAmount += ticket.netAmount
            pnrGroups[pnr].totalReceivedAmount += ticket.amountReceived
        })
        // Convert to tripSummary format
        const transactionSummaryData = Object.values(pnrGroups).map((group, index) => {
            // Combine all confirmed seat numbers
            const confirmedSeatNumbers = group.confirmedTickets.flatMap(ticket => ticket.seats)
            // Combine all cancelled seat numbers
            const cancelledSeatNumbers = group.cancelledTickets.flatMap(ticket => ticket.seats)
            // Calculate totals
            const totalFare = group.confirmedTickets.reduce((sum, ticket) => sum + ticket.totalFare, 0)
            const totalGST = group.confirmedTickets.reduce((sum, ticket) => sum + ticket.totalGST, 0)
            const totalCommission = group.confirmedTickets.reduce((sum, ticket) => sum + ticket.totalCommission, 0)
            const totalPenalty = group.cancelledTickets.reduce((sum, ticket) => sum + (ticket.cancelledTotalPenalty || 0), 0)
            const totalPenaltyCommission = group.cancelledTickets.reduce((sum, ticket) => sum + (ticket.cancelledPenaltyCommission || 0), 0)
            return {
                ticket_id: group.ticket_id,
                pnr: group.pnr,
                start_date: group.start_date,
                netAmount: group.totalConfirmedAmount + group.totalCancelledAmount,
                recievedAmount: group.totalReceivedAmount,
                balanceDue: (group.totalConfirmedAmount + group.totalCancelledAmount) - group.totalReceivedAmount,
                tripInfo: {
                    origin: group.commonInfo.origin,
                    destination: group.commonInfo.destination,
                    tripDetails: group.commonInfo.tripDetails,
                    bookingDate: group.commonInfo.bookingDate,
                    startDate: group.commonInfo.startDate,
                    passengerDetails: group.commonInfo.passengerDetails,
                    confirmedSeatNumber: confirmedSeatNumbers,
                    cancelledSeatNumber: cancelledSeatNumbers,
                    ticketFare: totalFare,
                    ticketGST: totalGST,
                    ticketCommission: totalCommission,
                    confirmedNetAmount: group.totalConfirmedAmount,
                    ticketPenalty: totalPenalty,
                    ticketPenaltyCommission: totalPenaltyCommission,
                    cancelledNetAmount: group.totalCancelledAmount
                }
            }
        })
        
        return transactionSummaryData
    }

    const fetchAgentTransactions = (pnrs) => {
            fetchTransactionsByPnr(pnrs, (data) => {
                let isValidExpandedRowData = isValid(data) && (isValid(data['confirmed']) || isValid(data['cancelled']));
                let transactionSummaryData = transformTicketSummaryToAgentTransactionSummary({...data});
                setFormattedTransactionSummaryData(transactionSummaryData);
                setExpandedRowData(data);
                setIsValidExpandedRowData(isValidExpandedRowData);
            }, (error) => {
                console.log("error", error);
                setExpandedRowData(null);
                setIsValidExpandedRowData(false);
            });
        // }
    }

    useEffect(() => {
        if (expandRecord.pnrList && expandRecord.pnrList.length > 0) {
            fetchAgentTransactions(expandRecord.pnrList);
        }
    }, [expandRecord.pnrList]);

    // Helper function to calculate totals from data
    const calculateTotals = (data, type) => {
        if (!data || data.length === 0) {
            return type === 'confirmed' ? {
                totalFare: 0, totalGST: 0, totalCommission: 0, netAmount: 0, amountReceived: 0, balanceDue: 0
            } : {
                totalFare: 0, totalGST: 0, totalPenalty: 0, totalPenaltyCommission: 0, netAmount: 0, amountReceived: 0, balanceDue: 0
            };
        }

        return data.reduce((acc, record) => {
            if (type === 'confirmed') {
                acc.totalFare += (record.totalFare || 0);
                acc.totalGST += (record.totalGST || 0);
                acc.totalCommission += (record.totalCommission || 0);
                acc.netAmount += (record.netAmount || 0);
                acc.amountReceived += (record.amountReceived || 0);
                acc.balanceDue += (record.balanceDue || 0);
            } else {
                acc.totalFare += (record.totalFare || 0);
                acc.totalGST += (record.totalGST || 0);
                acc.totalPenalty += (record.cancelledTotalPenalty || 0);
                acc.totalPenaltyCommission += (record.cancelledPenaltyCommission || 0);
                acc.netAmount += (record.netAmount || 0);
                acc.amountReceived += (record.amountReceived || 0);
                acc.balanceDue += (record.balanceDue || 0);
            }
            return acc;
        }, type === 'confirmed' ? {
            totalFare: 0, totalGST: 0, totalCommission: 0, netAmount: 0, amountReceived: 0, balanceDue: 0
        } : {
            totalFare: 0, totalGST: 0, totalPenalty: 0, totalPenaltyCommission: 0, netAmount: 0, amountReceived: 0, balanceDue: 0
        });
    };


    useEffect(() => {
        if (expandedRowData) {
            setFilteredConfirmedData(expandedRowData['confirmed'] || []);
            setFilteredCancelledData(expandedRowData['cancelled'] || []);
        }
    }, [expandedRowData]);

    // Calculate totals whenever filtered data changes
    useEffect(() => {
        const confirmed = calculateTotals(filteredConfirmedData, 'confirmed');
        const cancelled = calculateTotals(filteredCancelledData, 'cancelled');
        
        setConfirmedTotals(confirmed);
        setCancelledTotals(cancelled);
    }, [filteredConfirmedData, filteredCancelledData]);

    const renderStatus = (value) => {
        return <Tag color={value === 'Paid' ? 'green' : value === 'Due' ? 'red' : 'orange'}>{value}</Tag>;
    }

    const renderPnrWithTooltip = (pnr, record) => {
        let tripDetails = record.tripDetails;

        let origin = null;
        let destination = null;

        if (tripDetails) {
            const match = tripDetails.match(/\(([^>]+)\s*>\s*([^)]+)\)/);
            if (match) {
                origin = match[1].trim();
                destination = match[2].trim();
            }
        }        
        const tooltipContent = (
            <div>
                <div><strong>Booked By:</strong> {expandRecord.name || 'N/A'}</div>
                <div><strong>Seat Numbers:</strong> {Array.isArray(record.seats) ? record.seats.join(', ') : record.seats || 'N/A'}</div>
                <div><strong>Pass Details:</strong> {record.passengerDetails || 'N/A'}</div>
                {isValid(origin) && <div><strong>Origin:</strong> {origin}</div>}
                {isValid(destination) && <div><strong>Destination:</strong> {destination}</div>}
                <div><strong>Booking Time:</strong> {getLocalDateAndTime(record.bookingDate)}</div>
                <div><strong>Amount Paid:</strong> {record.totalFare + record.totalGST}</div>
                {isValid(record.cancelledTotalPenalty) && <div><strong>Total Penalty:</strong> {record.cancelledTotalPenalty}</div>}
                {isValid(record.cancelledPenaltyCommission) && <div><strong>Total Penalty Commission:</strong> {record.cancelledPenaltyCommission}</div>}
                {isValid(record.remarks)&&<div><strong>Remarks : </strong>{record.remarks}</div>}
            </div>
        );

        return (
            <>
            <Tooltip title={tooltipContent} placement="top">
                <a style={{ color: 'blue', textDecoration: 'underline' }}>
                    {pnr}
                </a>
            </Tooltip>
            {isValid(record.remarks)&&<><br/><span>(Remarks: {record.remarks})</span></>}
            </>
        );
    }

    const handleAgentPayment = (e, record, agentType) => {
        setSelectedAgentForPayment({
            id: record.agent_id,
            name: record.name,
            branch: record.branch,
            privilege: record.agent_privilege,
            agent_type: agentType,
            transactionSummaryData: formattedTransactionSummaryData
        });
        setIsPaymentModalVisible(true);
    };
    

    const expandedCommonColumns = [
        {title: 'DOJ',dataIndex: 'start_date',key: 'start_date',width: 20,align: 'center', sorter: (a, b) => moment(a.start_date).unix() - moment(b.start_date).unix(), render: (value) => getReadableDate(value)},
        {title: 'PNR',dataIndex: 'pnr',key: 'pnr',width: 40,align: 'center', render: (value, record) => renderPnrWithTooltip(value, record)},
        {title: 'Passenger Details',dataIndex: 'passengerDetails',key: 'passengerDetails',width: 60,align: 'center'},
        {title: 'Seats',dataIndex: 'seats',key: 'seats',width: 30,align: 'center', render: (value) => Array.isArray(value) ? value.join(', ') : value},
        {title: 'Trip Details',dataIndex: 'tripDetails',key: 'tripDetails',width: 200,align: 'center'},
        {title: 'Booking Date',dataIndex: 'bookingDate',key: 'bookingDate',width: 30,align: 'center', render: (value) => getReadableDate(value)},
        {title: "Tot Fare",dataIndex: 'totalFare',key: 'totalFare',width: 40,align: 'center',render:(value)=>roundNumber(value)},
        {title: "Tot GST",dataIndex: 'totalGST',key: 'totalGST',width: 40,align: 'center',render:(value)=>roundNumber(value)},
    ];

    const expandedConfirmedColumns = [...expandedCommonColumns,
        {title: "Tot Comm",dataIndex: 'totalCommission',key: 'totalCommission',width: 40,align: 'center',render:(value)=>roundNumber(value)},
        {title: (
            <Tooltip title="Net Amount = Total Fare + Total GST - Total Commission">
                <span>Net Amount</span>
            </Tooltip>
        ),dataIndex: 'netAmount',key: 'netAmount',width: 40,align: 'center',render:(value)=>roundNumber(value)},
        {title: 'Amount Received',dataIndex: 'amountReceived',key: 'amountReceived',width: 40,align: 'center',render:(value)=>roundNumber(value)},
        {title: (
            <Tooltip title="Balance Due = Net Amount - Amount Received">
                <span>Balance Due</span>
            </Tooltip>
        ),dataIndex: 'balanceDue',key: 'balanceDue',width: 40,align: 'center',render:(value)=>roundNumber(value)},
        {title: 'Status',dataIndex: 'status',key: 'status',width: 40,align: 'center', render: (value) => renderStatus(value), filters: [
            { text: 'Paid', value: 'Paid' },
            { text: 'Due', value: 'Due' },
            { text: 'Partially Paid', value: 'Partially Paid' }
        ], onFilter: (value, record) => record.status === value}
    ];
    const expandedCancelledColumns = [...expandedCommonColumns, 
        {title: 'Cancel Date',dataIndex: 'cancelDate',key: 'cancelDate',width: 30,align: 'center', render: (value) => getReadableDate(value)},
        {title: 'Total Penalty',dataIndex: 'cancelledTotalPenalty',key: 'cancelledTotalPenalty',width: 30,align: 'center'},
        {title: 'Total Penalty Comm',dataIndex: 'cancelledPenaltyCommission',key: 'cancelledPenaltyCommission',width: 30,align: 'center'},
        {title: (
            <Tooltip title="Net Amount = Total Penalty - Total Penalty Commission">
                <span>Net Amount</span>
            </Tooltip>
        ),dataIndex: 'netAmount',key: 'netAmount',width: 30,align: 'center'},
        {title: 'Amount Received',dataIndex: 'amountReceived',key: 'amountReceived',width: 30,align: 'center'},
        {title: (
            <Tooltip title="Balance Due = Net Amount - Amount Received">
                <span>Balance Due</span>
            </Tooltip>
        ),dataIndex: 'balanceDue',key: 'balanceDue',width: 30,align: 'center'},
        {title: 'Status',dataIndex: 'status',key: 'status',width: 40,align: 'center', render: (value) => renderStatus(value), filters: [
            { text: 'Paid', value: 'Paid' },
            { text: 'Due', value: 'Due' }
        ], onFilter: (value, record) => record.status === value}
    ];

    // Helper function to get filter information for display
    const getFilterInfo = (originalData, filteredData, dataType) => {
        const totalRecords = originalData?.length || 0;
        const filteredRecords = filteredData?.length || 0;
        const isFiltered = totalRecords !== filteredRecords;
        
        return {
            totalRecords,
            filteredRecords,
            isFiltered,
            filterPercentage: totalRecords > 0 ? Math.round((filteredRecords / totalRecords) * 100) : 0
        };
    };

    // Prints via a hidden same-document iframe (react-to-print) — the
    // window.open('', '_blank') + document.write approach fails on Android Chrome
    const printAgentLedger = useReactToPrint({
        contentRef: printRef,
        documentTitle: `Agent Ledger - ${expandRecord.name}`,
    });

    return (
        <div style={{ padding: '16px', maxWidth: '100%', overflow: 'hidden', backgroundColor: 'white' }} className="agent-ledger-expand-table">
        <Row gutter={16}>
            <Col span={12}>
                <Row>
                    <strong style={{ fontWeight: 'bold', fontSize: '18px' }}>Agent Name: {expandRecord.name}</strong>
                </Row>
                <Row>
                    <strong>Balance Due: ₹{expandRecord.balanceDue?.toLocaleString()}</strong>
                </Row>
            </Col>
        </Row>
        <Row gutter={16}>
            <Col span={10}>
                <Button
                    type="primary"
                    onClick={(e) => handleAgentPayment(e, expandRecord, agentType)}
                    style={{ float: 'right' }}
                >
                    Agent Payment
                </Button>
            </Col>
            <Col>
                <Button
                    type="primary"
                    onClick={(e) => printAgentLedger()}
                    style={{ float: 'right' }}
                >
                    <PrinterOutlined style = {{fontSize: '18px'}} /> Print
                </Button>
            </Col>
        </Row>
        
        {/* Printable content — hidden on screen only, so it renders in the print iframe */}
        <div ref={printRef} className="agent-ledger-print-content">
            <div className="header">
                <h1>{global.operatorName} - Agent Ledger Report</h1>
            </div>
            
            <div className="agent-info">
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ width: '50%' }}>
                        <p><strong>Date Range:</strong> {getReadableDate(dateRange[0])} - {getReadableDate(dateRange[1])}</p>
                    </div>
                    <div style={{ width: '50%' }}>
                        <p><strong>Balance Due:</strong> ₹{expandRecord.balanceDue?.toLocaleString()}</p>
                    </div>
                </div>
                <div style={{ width: '100%' }}>
                    <p><strong>Name:</strong> {expandRecord.name}</p>
                </div>
            </div>
            
            {isValidExpandedRowData && isValidArrayWithValues(filteredConfirmedData) && (
                <div className="section">
                    <h3 className="section-title">Confirmed PNRs</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>DOJ</th>
                                <th>PNR</th>
                                <th>Passenger Details</th>
                                <th>Seats</th>
                                <th>Trip Details</th>
                                <th>Booking Date</th>
                                <th>Total Fare</th>
                                <th>Total GST</th>
                                <th>Total Commission</th>
                                <th>Net Amount</th>
                                <th>Amount Received</th>
                                <th>Balance Due</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredConfirmedData.map((record, index) => (
                                <tr key={index}>
                                    <td>{getReadableDate(record.start_date)}</td>
                                    <td>{record.pnr} {isValid(record.remarks)&&<span>(Remarks: {record.remarks})</span>}</td>
                                    <td>{record.passengerDetails}</td>
                                    <td>{Array.isArray(record.seats) ? record.seats.join(', ') : record.seats}</td>
                                    <td>{record.tripDetails}</td>
                                    <td>{getReadableDate(record.bookingDate)}</td>
                                    <td>₹{record.totalFare}</td>
                                    <td>₹{record.totalGST}</td>
                                    <td>₹{record.totalCommission}</td>
                                    <td>₹{record.netAmount}</td>
                                    <td>₹{record.amountReceived}</td>
                                    <td>₹{record.balanceDue}</td>
                                    <td>{record.status}</td>
                                </tr>
                            ))}
                            {/* Summary Row for Confirmed PNRs */}
                            <tr className="total-row" style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                                <td colSpan="6" style={{ textAlign: 'center', fontWeight: 'bold' }}>TOTAL</td>
                                <td style={{ fontWeight: 'bold' }}>₹{confirmedTotals.totalFare.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{confirmedTotals.totalGST.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{confirmedTotals.totalCommission.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{confirmedTotals.netAmount.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{confirmedTotals.amountReceived.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{confirmedTotals.balanceDue.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>-</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
            
            {isValidExpandedRowData && isValidArrayWithValues(filteredCancelledData) && (
                <div className="section">
                    <h3 className="section-title">Cancelled PNRs</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>DOJ</th>
                                <th>PNR</th>
                                <th>Passenger Details</th>
                                <th>Seats</th>
                                <th>Trip Details</th>
                                <th>Booking Date</th>
                                <th>Total Fare</th>
                                <th>Total GST</th>
                                <th>Cancel Date</th>
                                <th>Total Penalty</th>
                                <th>Total Penalty Comm</th>
                                <th>Net Amount</th>
                                <th>Amount Received</th>
                                <th>Balance Due</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCancelledData.map((record, index) => (
                                <tr key={index}>
                                    <td>{getReadableDate(record.start_date)}</td>
                                    <td>{record.pnr}</td>
                                    <td>{record.passengerDetails}</td>
                                    <td>{Array.isArray(record.seats) ? record.seats.join(', ') : record.seats}</td>
                                    <td>{record.tripDetails}</td>
                                    <td>{getReadableDate(record.bookingDate)}</td>
                                    <td>₹{record.totalFare}</td>
                                    <td>₹{record.totalGST}</td>
                                    <td>{getReadableDate(record.cancelDate)}</td>
                                    <td>₹{record.cancelledTotalPenalty}</td>
                                    <td>₹{record.cancelledPenaltyCommission}</td>
                                    <td>₹{record.netAmount}</td>
                                    <td>₹{record.amountReceived}</td>
                                    <td>₹{record.balanceDue}</td>
                                    <td>{record.status}</td>
                                </tr>
                            ))}
                            {/* Summary Row for Cancelled PNRs */}
                            <tr className="total-row" style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                                <td colSpan="9" style={{ textAlign: 'center', fontWeight: 'bold' }}>TOTAL</td>
                                <td style={{ fontWeight: 'bold' }}>₹{cancelledTotals.totalPenalty.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{cancelledTotals.totalPenaltyCommission.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{cancelledTotals.netAmount.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{cancelledTotals.amountReceived.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>₹{cancelledTotals.balanceDue.toLocaleString()}</td>
                                <td style={{ fontWeight: 'bold' }}>-</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
        </div>
        {
            isValidExpandedRowData && isValidArrayWithValues(expandedRowData['confirmed']) && (
                <div>
                    <Row>
                        <Col>
                            Confirmed PNRs
                        </Col>
                    </Row>
                    <div style={{ width: '100%', maxWidth: '100%' }}>
                        <Table
                            columns={expandedConfirmedColumns}
                            dataSource={expandedRowData['confirmed']}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            rowClassName={(record, index) => index % 2 === 1 ? 'alternating-row' : ''}
                            pagination={{
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                defaultPageSize: 100
                            }}
                            onChange={(pagination, filters, sorter) => {
                                // Generic filter handler that works with any combination of filters
                                let filteredData = expandedRowData['confirmed'] || [];
                                
                                // Track active filters for reporting
                                const confirmedFilters = {};
                                Object.keys(filters).forEach(filterKey => {
                                    if (filters[filterKey] && filters[filterKey].length > 0) {
                                        confirmedFilters[filterKey] = filters[filterKey];
                                        filteredData = filteredData.filter(record => {
                                            const filterValues = filters[filterKey];
                                            const recordValue = record[filterKey];
                                            
                                            // Handle different data types and filter logic
                                            if (Array.isArray(filterValues)) {
                                                return filterValues.includes(recordValue);
                                            } else if (typeof filterValues === 'string') {
                                                return recordValue === filterValues;
                                            }
                                            return true;
                                        });
                                    }
                                });
                                setFilteredConfirmedData(filteredData);
                            }}
                        />
                    </div>
                </div>
            )
        }
        {
            isValidExpandedRowData && isValidArrayWithValues(expandedRowData['cancelled']) && (
                <div>
                    <Row>
                        <Col>
                            Cancelled PNRs
                        </Col>
                    </Row>
                    <div style={{ width: '100%', maxWidth: '100%' }}>
                        <Table
                            columns={expandedCancelledColumns}
                            dataSource={expandedRowData['cancelled']}
                            scroll={{ x: 'max-content' }}
                            size="small"
                            rowClassName={(record, index) => index % 2 === 1 ? 'alternating-row' : ''}
                            pagination={{
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                defaultPageSize: 10
                            }}
                            onChange={(pagination, filters, sorter) => {
                                // Generic filter handler that works with any combination of filters
                                let filteredData = expandedRowData['cancelled'] || [];
                                
                                // Track active filters for reporting
                                const cancelledFilters = {};
                                Object.keys(filters).forEach(filterKey => {
                                    if (filters[filterKey] && filters[filterKey].length > 0) {
                                        cancelledFilters[filterKey] = filters[filterKey];
                                        filteredData = filteredData.filter(record => {
                                            const filterValues = filters[filterKey];
                                            const recordValue = record[filterKey];
                                            
                                            // Handle different data types and filter logic
                                            if (Array.isArray(filterValues)) {
                                                return filterValues.includes(recordValue);
                                            } else if (typeof filterValues === 'string') {
                                                return recordValue === filterValues;
                                            }
                                            return true;
                                        });
                                    }
                                });
                                setFilteredCancelledData(filteredData);
                            }}
                        />
                    </div>
                </div>
            )
        }
    </div>
    )
}